import React, { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { css, cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N80, N900 } from '@atlaskit/theme/colors';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ArrowUpIcon from '@atlaskit/icon/core/migration/arrow-up';
import ArrowDownIcon from '@atlaskit/icon/core/migration/arrow-down';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n';

import { TaskColumn, SortTypes, SortOptions } from './tasksDefinitions';
import {
	CenteredContainer,
	CenteredContainerCompiled,
	ColumnHeaderContainer,
	IconButton,
} from './presentationalComponents';

const i18nBase = defineMessages({
	tasksHomeTitle: {
		id: 'home.views.tasks.title',
		defaultMessage: 'Tasks',
		description: "Header for the user's list of tasks",
	},
	taskDescColumn: {
		id: 'home.views.tasks.description.column',
		defaultMessage: 'Description',
		description: 'table column header for the description of a task',
	},
	taskPageColumn: {
		id: 'home.views.tasks.page.column',
		defaultMessage: 'Page',
		description: 'table column header for the page a task is on',
	},
	taskDueDateColumn: {
		id: 'home.views.tasks.date.column',
		defaultMessage: 'Due Date',
		description: 'table column header for the date a task is due',
	},
	taskCreatorColumn: {
		id: 'home.views.tasks.creator.column',
		defaultMessage: 'Creator',
		description: 'table column header for the creator of a task',
	},
	taskAssigneeColumn: {
		id: 'home.views.tasks.assignee.column',
		defaultMessage: 'Assignee',
		description: 'table column header for the assignee of a task',
	},
	taskSpaceColumn: {
		id: 'home.views.tasks.space.column',
		defaultMessage: 'Space',
		description: 'table column header for the space a task is on',
	},
	taskSortingUnavailable: {
		id: 'home.views.tasks.sorting.unavailable',
		defaultMessage: 'Sorting not available for creator, assignee, or space',
		description: 'informs the user we cannot sort tasks by its creator, assignee, or space',
	},
	taskSortAscending: {
		id: 'home.views.tasks.sort.ascending',
		defaultMessage: 'Sort from A -> Z',
		description: 'tooltip to tell the user they can sort alphabetically from A to Z',
	},
	taskSortDescending: {
		id: 'home.views.tasks.sort.descending',
		defaultMessage: 'Sort from Z -> A',
		description: 'tooltip to tell the user they can sort reverse alphabetically from Z to A',
	},
	taskSortSoonest: {
		id: 'home.views.tasks.sort.soonest',
		defaultMessage: 'Sort from Soonest -> Furthest from now',
		description: 'tooltip to tell the user they can sort from soonest to furthest from now',
	},
	taskSortFurthest: {
		id: 'home.views.tasks.sort.furthest',
		defaultMessage: 'Sort from Furthest from now -> Soonest',
		description: 'tooltip to tell the user they can sort reverse furthest from now to soonest',
	},
});

const i18nLiveDocs = defineMessages({
	taskPageColumn: {
		id: 'home.views.tasks.page.column.livedocs',
		defaultMessage: 'Page or Doc',
		description: 'Updated version of home.views.tasks.page.column',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const columnHeaderContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		height: 24,
		width: 24,
	},
	// alignItems: "flex-start",
});

const iconButtonStyles = cssMap({
	default: {
		border: 'none',
		outline: 'none',
		background: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		cursor: 'pointer',
		padding: 0,
		height: 24,
		width: 24,
		color: `${token('color.icon.subtle', N80)}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:hover': {
			color: `${token('color.icon', N900)}`,
		},
	},
	disabled: { cursor: 'not-allowed' },
});

const getTranslatedColumn = (name: TaskColumn) => {
	switch (name) {
		case TaskColumn.Page:
			return <FormattedMessage {...i18n.taskPageColumn} />;
		case TaskColumn.Space:
			return <FormattedMessage {...i18n.taskSpaceColumn} />;
		case TaskColumn.Assignee:
			return <FormattedMessage {...i18n.taskAssigneeColumn} />;
		case TaskColumn.Creator:
			return <FormattedMessage {...i18n.taskCreatorColumn} />;
		default:
			return <FormattedMessage {...i18n.taskPageColumn} />;
	}
};

const DEFAULT_COLUMNS: Array<TaskColumn> = [
	TaskColumn.Page,
	TaskColumn.Space,
	TaskColumn.Assignee,
	TaskColumn.Creator,
];

const getDropdownItems = (selectedCol: string, setMainColumn: (column: TaskColumn) => void) => {
	const availableCols = DEFAULT_COLUMNS.filter((item) => item !== selectedCol);

	return availableCols.map((col, idx) => {
		return (
			<DropdownItem key={idx} onClick={() => setMainColumn(col)}>
				{col.charAt(0).toUpperCase() + col.slice(1)}
			</DropdownItem>
		);
	});
};

const getDateTooltip = (sortOption: SortOptions) => {
	return sortOption === SortOptions.Ascending ? (
		<FormattedMessage {...i18n.taskSortFurthest} />
	) : (
		<FormattedMessage {...i18n.taskSortSoonest} />
	);
};

const getMainSortTooltip = (column: TaskColumn, sortOption: SortOptions) => {
	if (column === TaskColumn.Page) {
		return sortOption === SortOptions.Ascending ? (
			<FormattedMessage {...i18n.taskSortDescending} />
		) : (
			<FormattedMessage {...i18n.taskSortAscending} />
		);
	} else {
		return <FormattedMessage {...i18n.taskSortingUnavailable} />;
	}
};

const SortIcon = ({
	sortOption,
	label,
}: {
	sortOption: SortOptions;
	label: string; // Identifier to add to icon. Used to identify component for tests.
}) => {
	return sortOption === SortOptions.Ascending ? (
		<ArrowUpIcon label={`ascending sorting icon ${label}`} LEGACY_size="small" />
	) : (
		<ArrowDownIcon label={`descending sorting icon ${label}`} LEGACY_size="small" />
	);
};

export const useTasksTableHead = ({
	mainColumn,
	setMainColumn,
	setSortOption,
	setSortType,
}: {
	mainColumn: TaskColumn;
	setMainColumn: (column: TaskColumn) => void;
	setSortOption: (sortOption: SortOptions) => void;
	setSortType: (sortType: SortTypes) => void;
}) => {
	const [pageSortOption, setPageSortOption] = useState<SortOptions>(SortOptions.Ascending);
	const [dateSortOption, setDateSortOption] = useState<SortOptions>(SortOptions.Ascending);

	const onMainSortClick = () => {
		// Don't allow sorting if it's not the page column
		if (mainColumn !== TaskColumn.Page) return;
		const option =
			pageSortOption === SortOptions.Ascending ? SortOptions.Descending : SortOptions.Ascending;

		setPageSortOption(option);
		setSortType(SortTypes.Page);
		setSortOption(option);
	};

	const onDateSortClick = () => {
		const option =
			dateSortOption === SortOptions.Ascending ? SortOptions.Descending : SortOptions.Ascending;

		setDateSortOption(option);
		setSortType(SortTypes.Date);
		setSortOption(option);
	};

	if (fg('confluence_frontend_home_compiled_migration')) {
		return {
			cells: [
				{
					key: 'task-description-col',
					content: <FormattedMessage {...i18n.taskDescColumn} />,
				},
				{
					key: 'task-page-col',
					content: (
						<div css={columnHeaderContainerStyles}>
							<CenteredContainerCompiled>
								{getTranslatedColumn(mainColumn)}
								<Tooltip content={getMainSortTooltip(mainColumn, pageSortOption)}>
									{(tooltipProps) => (
										// eslint-disable-next-line @atlaskit/design-system/no-html-button
										<button
											{...tooltipProps}
											css={[
												iconButtonStyles['default'],
												mainColumn !== TaskColumn.Page && iconButtonStyles['disabled'],
											]}
											onClick={onMainSortClick}
										>
											<SortIcon sortOption={pageSortOption} label="page" />
										</button>
									)}
								</Tooltip>
							</CenteredContainerCompiled>
							<DropdownMenu trigger="">
								<DropdownItemGroup>
									<DropdownItem isDisabled>Column</DropdownItem>
									{getDropdownItems(mainColumn, setMainColumn)}
								</DropdownItemGroup>
							</DropdownMenu>
						</div>
					),
				},
				{
					key: 'task-due-date-col',
					content: (
						<CenteredContainerCompiled>
							<FormattedMessage {...i18n.taskDueDateColumn} />
							<Tooltip content={getDateTooltip(dateSortOption)}>
								{(tooltipProps) => (
									// eslint-disable-next-line @atlaskit/design-system/no-html-button
									<button
										css={[
											iconButtonStyles['default'],
											mainColumn !== TaskColumn.Page && iconButtonStyles['disabled'],
										]}
										{...tooltipProps}
										onClick={onDateSortClick}
									>
										<SortIcon sortOption={dateSortOption} label="date" />
									</button>
								)}
							</Tooltip>
						</CenteredContainerCompiled>
					),
				},
			],
		};
	}

	return {
		cells: [
			{
				key: 'task-description-col',
				content: <FormattedMessage {...i18n.taskDescColumn} />,
			},
			{
				key: 'task-page-col',
				content: (
					<ColumnHeaderContainer>
						<CenteredContainer>
							{getTranslatedColumn(mainColumn)}
							<Tooltip content={getMainSortTooltip(mainColumn, pageSortOption)}>
								{(tooltipProps) => (
									<IconButton
										{...tooltipProps}
										isDisabled={mainColumn !== TaskColumn.Page}
										onClick={onMainSortClick}
									>
										<SortIcon sortOption={pageSortOption} label="page" />
									</IconButton>
								)}
							</Tooltip>
						</CenteredContainer>
						<DropdownMenu trigger="">
							<DropdownItemGroup>
								<DropdownItem isDisabled>Column</DropdownItem>
								{getDropdownItems(mainColumn, setMainColumn)}
							</DropdownItemGroup>
						</DropdownMenu>
					</ColumnHeaderContainer>
				),
			},
			{
				key: 'task-due-date-col',
				content: (
					<CenteredContainer>
						<FormattedMessage {...i18n.taskDueDateColumn} />
						<Tooltip content={getDateTooltip(dateSortOption)}>
							{(tooltipProps) => (
								<IconButton isDisabled={false} {...tooltipProps} onClick={onDateSortClick}>
									<SortIcon sortOption={dateSortOption} label="date" />
								</IconButton>
							)}
						</Tooltip>
					</CenteredContainer>
				),
			},
		],
	};
};
